import * as pc from 'playcanvas';

const DEG = Math.PI / 180;

function multiply(a, b, out = new pc.Quat()) {
  return out.set(
    a.w * b.x + a.x * b.w + a.y * b.z - a.z * b.y,
    a.w * b.y - a.x * b.z + a.y * b.w + a.z * b.x,
    a.w * b.z + a.x * b.y - a.y * b.x + a.z * b.w,
    a.w * b.w - a.x * b.x - a.y * b.y - a.z * b.z
  );
}

function yxzQuaternion(x, y, z, out = new pc.Quat()) {
  const c1 = Math.cos(x / 2);
  const c2 = Math.cos(y / 2);
  const c3 = Math.cos(z / 2);
  const s1 = Math.sin(x / 2);
  const s2 = Math.sin(y / 2);
  const s3 = Math.sin(z / 2);
  return out.set(
    s1 * c2 * c3 + c1 * s2 * s3,
    c1 * s2 * c3 - s1 * c2 * s3,
    c1 * c2 * s3 - s1 * s2 * c3,
    c1 * c2 * c3 + s1 * s2 * s3
  );
}

export class SensorLook {
  constructor() {
    this.active = false;
    this.available = typeof window.DeviceOrientationEvent !== 'undefined';
    this.reading = null;
    this.needsRecenter = true;
    this.angleOverride = null;
    this.headingOffset = new pc.Quat();
    this.forward = new pc.Vec3();
    this.orientation = new pc.Quat();
    this.qDevice = new pc.Quat();
    this.qScreen = new pc.Quat();
    this.qCamera = new pc.Quat(-Math.sqrt(0.5), 0, 0, Math.sqrt(0.5));
    this.onOrientation = this.onOrientation.bind(this);
  }

  async request() {
    if (!this.available) return false;
    let allowed = true;
    try {
      if (typeof window.DeviceOrientationEvent.requestPermission === 'function') {
        allowed = (await window.DeviceOrientationEvent.requestPermission()) === 'granted';
      }
    } catch {
      allowed = false;
    }
    if (!allowed) return false;
    window.addEventListener('deviceorientation', this.onOrientation, true);
    this.active = true;
    return true;
  }

  stop() {
    window.removeEventListener('deviceorientation', this.onOrientation, true);
    this.active = false;
    this.reading = null;
    this.needsRecenter = true;
  }

  // Recenter on where the device is actually pointing (works for portrait and landscape,
  // either rotation): measure the camera's heading and cancel it.
  recenter() {
    this.needsRecenter = true;
  }

  onOrientation(event) {
    if (event.alpha === null) return;
    this.reading = {
      alpha: Number(event.alpha || 0),
      beta: Number(event.beta || 0),
      gamma: Number(event.gamma || 0)
    };
  }

  // Orientation for a given screen angle (0 portrait, 90 / 270 landscape).
  compose(angle, out) {
    const r = this.reading;
    yxzQuaternion(r.beta * DEG, r.alpha * DEG, -r.gamma * DEG, this.qDevice);
    // Same as the reference three.js formula: rotate about +Z (PlayCanvas BACK) by -angle.
    // PlayCanvas FORWARD is -Z, so using FORWARD here flipped landscape upside down.
    this.qScreen.setFromAxisAngle(pc.Vec3.BACK, -angle);
    multiply(this.qDevice, this.qCamera, out);
    return multiply(out, this.qScreen, out).normalize();
  }

  // Which way is the phone actually held? Returns the screen angle whose camera "up" is closest
  // to world up. Used when the page is stuck in portrait (rotation lock on, or iPhone) but the
  // phone is sideways in a viewer.
  bestScreenAngle(candidates = [0, 90, 270]) {
    if (!this.active || !this.reading) return null;
    this.probe = this.probe || new pc.Quat();
    this.probeUp = this.probeUp || new pc.Vec3();
    let best = null;
    for (const angle of candidates) {
      this.compose(angle, this.probe).transformVector(pc.Vec3.UP, this.probeUp);
      if (!best || this.probeUp.y > best.y) best = { angle, y: this.probeUp.y };
    }
    return best;
  }

  update(entity) {
    if (!this.active || !this.reading) return false;
    const screenAngle = this.angleOverride ?? Number(screen.orientation?.angle ?? window.orientation ?? 0);
    this.compose(screenAngle, this.orientation);
    if (this.needsRecenter) {
      this.orientation.transformVector(pc.Vec3.FORWARD, this.forward);
      const heading = Math.atan2(-this.forward.x, -this.forward.z);
      this.headingOffset.setFromAxisAngle(pc.Vec3.UP, -heading / DEG);
      this.needsRecenter = false;
    }
    multiply(this.headingOffset, this.orientation, this.orientation).normalize();
    entity.setRotation(this.orientation);
    return true;
  }
}
